import type { AxiosInstance } from "axios";
import { APIErrorResponse, API_ERROR_RESPONSE } from "./types/api-error";
import {
    StateLookupRequest,
    StateLookupResponse,
    STATE_LOOKUP_RESPONSE,
} from "./types/state-lookup-model";
import axios from 'axios';
import { SaveQuoteRequest, SaveQuoteResponse, SAVE_QUOTE_RESPONSE } from "./types";
import { ClientConfigResponse, CLIENT_CONFIG_RESPONSE } from "./types/config-response";
import { PartnerDetailsResponse, PARTNER_DETAILS_RESPONSE } from "./types/partner-details";
import { SearchQuoteRequest, SearchQuoteResponse, SEARCH_QUOTE_RESPONSE } from "./types";
import { BusinessKeywordResponse, BUSINESS_KEYWORD_RESPONSE } from "./types/business-keyword-model";
import { CarrierRecommendationResponse, CARRIER_RECOMMENDATION_RESPONSE, MultipleCarrierRecommendationResponse, MULTIPLE_CARRIER_RECOMMENDATION_RESPONSE } from "./types/carrier-recommendation-model";
import { LoggingResourceModel } from "./types/quote-request";
import { UpsertPartnerQuoteInfoRequest, UpsertPartnerQuoteInfoResponse, UPSERT_PARNTER_QUOTE_INFO_RESPONSE } from "./types";
import { AgentNotesResponse, AgentNotesRequest, AGENT_NOTES_RESPONSE, AddAgentNoteRequest } from "./types/agent-notes-modal";
import { UpsertPartnerQuoteInfoWithQuoteOutcomeRequest } from "./types/quote-outcome";
import { ALL_CARRIER_RECOMMENDATION_RESPONSE, AllCarrierRecommendationResponse } from "./types/all-carrier-recommendation-response";
import { UPSERT_PARNTER_QUOTE_INFO_RESPONSE_FAILURE, UpsertPartnerQuoteInfoResponseFailure } from "./types/upsert-partner-quote-info-response";
import { BindingRestrictionsResponse, BINDING_RESTRICTIONS_RESPONSE } from "./types/binding-restrictions-model";

export const BASE_APP_PAth = "/business-insurance";
export const BASE_URL = window.location.origin + BASE_APP_PAth;
export const API_ERROR_MESSAGE = "There was a problem while processing the information you submitted. "
    + "Please call an (866)509-9444 and an insurance counselor can assist you with completing your quote or setting up your policy.";


// Set the name of the cookie to send as CSRF token
axios.defaults.xsrfCookieName = '_h_lpd_af';

// Set the name of the header to send as CSRF token
axios.defaults.xsrfHeaderName = 'x-lpd-af';

export const launchPadAPI = (api: AxiosInstance) => ({
    commercialAgentApi: {
        getClientConfig: (): Promise<ClientConfigResponse | APIErrorResponse> =>
            api.get(BASE_URL + "/api/clientconfigurations")
                .then(({ data }) => {
                    return {
                        data: data,
                        type: CLIENT_CONFIG_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        saveQuote: (saveQuoteRequest: SaveQuoteRequest, isComplete: boolean = false): Promise<SaveQuoteResponse | APIErrorResponse> =>
            api
                .post(
                    BASE_URL + (isComplete ? "/api/webquote/complete" : "/api/webquote"),
                    JSON.stringify(saveQuoteRequest),
                    { headers: { "Content-type": "application/json" } }
                )
                .then(({ data }) => {
                    return {
                        data: data,
                        type: SAVE_QUOTE_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        upsertPartnerQuoteInfo: (upsertPartnerQuoteInfoRequest: UpsertPartnerQuoteInfoRequest, authToken: string = ""): Promise<UpsertPartnerQuoteInfoResponse | APIErrorResponse> =>
            api
                .post(BASE_URL + "/api/UpsertPartnerQuote/UpsertPartnerQuoteInfo",
                    JSON.stringify(upsertPartnerQuoteInfoRequest),
                    {
                        headers: {
                            "Content-type": "application/json",
                            "Authorization": "Bearer " + authToken
                        },
                    })
                .then(({ data }) => {
                    return {
                        data: data,
                        type: UPSERT_PARNTER_QUOTE_INFO_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE
                    })
                ),
        upsertPartnerQuoteInfoWithQuoteOutcome: (upsertPartnerQuoteInfoRequest: UpsertPartnerQuoteInfoWithQuoteOutcomeRequest, authToken: string = ""): Promise<UpsertPartnerQuoteInfoResponse | APIErrorResponse | UpsertPartnerQuoteInfoResponseFailure> =>
            api
                .post(BASE_URL + "/api/UpsertPartnerQuote/UpsertPartnerQuoteInfo",
                    JSON.stringify(upsertPartnerQuoteInfoRequest),
                    {
                        headers: {
                            "Content-type": "application/json",
                            "Authorization": "Bearer " + authToken
                        },
                    })
                .then(({ data }) => {
                    if (data?.success === false && data.notices && data.notices.length > 0) {
                        return {
                            data: data,
                            type: UPSERT_PARNTER_QUOTE_INFO_RESPONSE_FAILURE,
                        }
                    }
                    return {
                        data: data,
                        type: UPSERT_PARNTER_QUOTE_INFO_RESPONSE,
                    };
                },
                ({ response }) => {
                const statusCode = response?.status || 500;
                const statusText = response?.statusText || "Error processing request";
                return {
                    ...(response || { data: {} }).data,
                    type: API_ERROR_RESPONSE,
                    statusCode,
                    statusText,
                };
                }
                ),
        getCarrierCodes: (isIncompleteOrDecline: boolean): Promise<PartnerDetailsResponse | APIErrorResponse> =>
            api.get(`${BASE_URL}/api/UpsertPartnerQuote/CarrierCodes?isIncompleteOrDecline=${isIncompleteOrDecline}`)
                .then(
                    ({ data }) => ({
                        data: data,
                        type: PARTNER_DETAILS_RESPONSE,
                    }),
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        getPartnerData: (): Promise<PartnerDetailsResponse | APIErrorResponse> =>
            api.get(BASE_URL + "/api/UpsertPartnerQuote/PartnerData")
                .then(({ data }) => {
                    return {
                        data: data,
                        type: PARTNER_DETAILS_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        getSearchQuote: (searchQuoteRequest: SearchQuoteRequest, authToken: string = ""): Promise<SearchQuoteResponse | APIErrorResponse> => {
            console.log("SearchQuoteRequest:", searchQuoteRequest);
            return api
                .post(
                    BASE_URL + "/api/searchquote/GetSearchQuote",
                    JSON.stringify(searchQuoteRequest),
                    {
                        headers: {
                            "Content-type": "application/json",
                            "Authorization": "Bearer " + authToken
                        },

                    }
                )
                .then(({ data }) => {
                    console.log("SearchQuoteResponse", data);
                    return {
                        data: data,
                        type: SEARCH_QUOTE_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                );
        },
        getBusinessKeywords: (lobcode: string): Promise<BusinessKeywordResponse | APIErrorResponse> =>
            api.get(BASE_URL + "/api/lookup/businesskeywords?lobcode=" + lobcode)
                .then(({ data }) => {
                    return {
                        data: data,
                        type: BUSINESS_KEYWORD_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    }
                    )
                ),
        getPrimaryBusinessKeywords: (): Promise<BusinessKeywordResponse | APIErrorResponse> =>
            api.get(BASE_URL + "/api/lookup/primarybusinesskeywords")
                .then(({ data }) => {
                    return {
                        data: data,
                        type: BUSINESS_KEYWORD_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    }
                    )
                ),
        getSecondaryBusinessKeywords: (primaryKeywordId: string): Promise<BusinessKeywordResponse | APIErrorResponse> =>
            api.get(BASE_URL + "/api/lookup/secondarybusinesskeywords?primaryKeywordId=" + primaryKeywordId)
                .then(({ data }) => {
                    return {
                        data: data,
                        type: BUSINESS_KEYWORD_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    }
                    )
                ),

        getCarrierRecommendation: (saveQuoteRequest: SaveQuoteRequest): Promise<CarrierRecommendationResponse | APIErrorResponse> =>
            api
                .post(BASE_URL + "/api/lookup/GetCarrierRecommendation",
                    JSON.stringify(saveQuoteRequest),
                    {
                        headers:
                            { "Content-type": "application/json" }
                    })
                .then(({ data }) => {
                    return {
                        data: data,
                        type: CARRIER_RECOMMENDATION_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    }
                    )
                ),
        stateLookup: (
            commercialAgentApiStateLookupRequest: StateLookupRequest
        ): Promise<StateLookupResponse | APIErrorResponse> =>
            api
                .get(BASE_URL + "/api/lookup/StateFromZipCode?zipCode=" +
                    commercialAgentApiStateLookupRequest.zipCode)
                .then(
                    ({ data }) => {
                        return {
                            data: data,
                            type: STATE_LOOKUP_RESPONSE,
                        };
                    },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        getAgentNotes: (agentNotesRequest: AgentNotesRequest, authToken: string = ""): Promise<AgentNotesResponse | APIErrorResponse> =>
            api
                .post(BASE_URL + "/api/marketplacequote/AgentNotes",
                    JSON.stringify(agentNotesRequest),
                    {
                        headers: {
                            "Content-type": "application/json",
                            "Authorization": "Bearer " + authToken
                        },
                    }
                )
                .then(
                    ({ data }) => {
                        return {
                            data: data,
                            type: AGENT_NOTES_RESPONSE,
                        };
                    },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        addAgentNote: (addAgentNotesRequest: AddAgentNoteRequest, authToken: string = ""): Promise<AgentNotesResponse | APIErrorResponse> =>
            api
                .post(BASE_URL + "/api/marketplacequote/AddAgentNote",
                    JSON.stringify(addAgentNotesRequest),
                    {
                        headers: {
                            "Content-type": "application/json",
                            "Authorization": "Bearer " + authToken
                        },
                    })
                .then(({ data }) => {
                    return {
                        data: data,
                        type: AGENT_NOTES_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        logError: (logRequest: LoggingResourceModel): Promise<string> =>
            api
                .post(BASE_URL + "/api/ErrorLog/LogError",
                    JSON.stringify(logRequest),
                    {
                        headers:
                            { "Content-type": "application/json" }
                    })
                .then((response) => {
                    console.log(response);
                    return "Success";
                }, (error) => {
                    console.log(error);
                    return "Error";
                }),
        getMultipleCarrierRecommendation: (saveQuoteRequest: SaveQuoteRequest): Promise<MultipleCarrierRecommendationResponse | APIErrorResponse> =>
            api
                .post(BASE_URL + "/api/lookup/GetMultipleCarrierRecommendation",
                    JSON.stringify(saveQuoteRequest),
                    {
                        headers:
                            { "Content-type": "application/json" }
                    })
                .then(({ data }) => {
                    return {
                        data: data,
                        type: MULTIPLE_CARRIER_RECOMMENDATION_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        getMultipleCarrierRecommendationUkw: (saveQuoteRequest: SaveQuoteRequest): Promise<MultipleCarrierRecommendationResponse | APIErrorResponse> =>
            api
                .post(BASE_URL + "/api/lookup/GetMultipleCarrierRecommendationUkw",
                    JSON.stringify(saveQuoteRequest),
                    {
                        headers:
                            { "Content-type": "application/json" }
                    })
                .then(({ data }) => {
                    return {
                        data: data,
                        type: MULTIPLE_CARRIER_RECOMMENDATION_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        getAllLobMultipleCarrierRecommendationUkw: (saveQuoteRequest: SaveQuoteRequest): Promise<AllCarrierRecommendationResponse | APIErrorResponse> =>
            api
                .post(BASE_URL + "/api/lookup/GetAllLobMultipleCarrierRecommendationUkw",
                    JSON.stringify(saveQuoteRequest),
                    {
                        headers:
                            { "Content-type": "application/json" }
                    })
                .then(({ data }) => {
                    return {
                        data: data,
                        type: ALL_CARRIER_RECOMMENDATION_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        getBusinessKeywordRecommendation: (keyword: string): Promise<BusinessKeywordResponse | APIErrorResponse> =>
            api.get(BASE_URL + "/api/lookup/GetBusinessKeywordRecommendation?keyword=" + keyword)
                .then(({ data }) => {
                    return {
                        data: data,
                        type: BUSINESS_KEYWORD_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                ),
        getBindingRestrictions: (zipCode: string): Promise<BindingRestrictionsResponse | APIErrorResponse> =>
            api.get(BASE_URL + "/api/lookup/BindingRestrictions?zipCode=" + encodeURIComponent(zipCode))
                .then(({ data }) => {
                    return {
                        data: data,
                        type: BINDING_RESTRICTIONS_RESPONSE,
                    };
                },
                    ({ response }) => ({
                        ...(response || { data: {} }).data,
                        type: API_ERROR_RESPONSE,
                    })
                )
    }
});
